/* eslint-disable */
import React, { useContext, useEffect, useLayoutEffect, useRef, useState, useMemo, SyntheticEvent, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import OlFormatWKT from 'ol/format/WKT';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { Dialog, useMediaQuery, useTheme } from '@mui/material';

// MUI Icons
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

// Custom Components
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItemDownload from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDownload';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogContext, { IDialogProps, DialogContextType } from '@/context/DialogContext/DialogContext';
import LoaderContext, { LoaderContextType } from '@/context/LoaderContext/LoaderContext';
import imgService from '@/services/imgService';
import Canvas from './Canvas';

// Types
import { ClosingDetails } from '@/@types/components/formController';

import { DCRecord } from '@/@types/lib/dataController';

type Clip = [number, number, number, number];
type Ratio = [number, number];
type Pair<A, B> = [A, B];
type Coordinates = Pair<number, number>[] | null;

const useWindowSize = () => {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};

const PT_RADIUS = 5;

// const IMAGE_WIDTH = 800;
// const IMAGE_HEIGHT = IMAGE_WIDTH * 0.6666;
const MIN_WIDTH = 400;

const canvasParentPaddingPixels = 0;

const PhotoCentricImageDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext) as DialogContextType;

  const { t } = useTranslation();
  const [b64, setB64] = useState<string>('');

  const [windowWidth, windowHeight] = useWindowSize();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [wrapperSize, setWrapperSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const { record, baseRecordPath, baseRecordId, onClose, dialogHidePolygons = false } = props;

  const [zoomClip, setZoomClip] = useState<Clip | null>(null);
  const [fullB64, setFullB64] = useState<string>('');
  const [initialB64, setInitialB64] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { toggleLoading } = useContext(LoaderContext) as LoaderContextType;
  const [isDrawing, setIsDrawing] = useState(false);
  const imageId: number | null = record ? (record.id as number) : null;

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  // Measure the real drawable area inside the dialog.
  // We use this to keep image/canvas inside bounds on resize and breakpoint changes.
  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setWrapperSize({ width: rect.width, height: rect.height });
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [smUp]);

  const [renderWidth, renderHeight] = useMemo(() => {
    if (!record) return [0, 0];

    const recordWidth = record.width as number;
    const recordHeight = record.height as number;

    const viewportMaxWidth = smUp ? Math.round(windowWidth * 0.5) : Math.round(windowWidth);
    const viewportMaxHeight = smUp ? Math.round(windowHeight * 0.7) : Math.max(0, windowHeight - 112);

    const availableWidth = wrapperSize.width > 0 ? Math.min(wrapperSize.width, viewportMaxWidth) : viewportMaxWidth;
    const availableHeight = wrapperSize.height > 0 ? Math.min(wrapperSize.height, viewportMaxHeight) : viewportMaxHeight;

    if (!availableWidth || !availableHeight || !recordWidth || !recordHeight) return [0, 0];

    // Preserve original image aspect ratio while fitting into available space.
    const scale = Math.min(availableWidth / recordWidth, availableHeight / recordHeight);
    return [Math.max(1, Math.round(recordWidth * scale)), Math.max(1, Math.round(recordHeight * scale))];
  }, [record, smUp, windowWidth, windowHeight, wrapperSize.width, wrapperSize.height]);

  const handleClose = (evt: SyntheticEvent) => {
    close({ dataChanged: false, action: 'cancel' });
  };

  const close = (result: ClosingDetails) => {
    if (result.dataChanged) {
      onClose({ dataChanged: true, action: result.action });
    }

    dialogContext.hideDialog();
  };

  const getCoordinates = (wkt: string) => {
    let coords: Coordinates = null;
    try {
      const WKT = new OlFormatWKT();
      const geom = WKT.readGeometry(wkt);
      // @ts-ignore
      coords = geom.getCoordinates()[0];
    } catch (err) {
      // pass
    }
    return coords;
  };

  /*const getClip = (coords: Coordinates) => {
    const { width, height } = record;
    if (coords) {
      let minX = width * 2;
      let minY = height * 2;
      let maxX = -minX;
      let maxY = -minY;
      coords.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      const dx = maxX - minX;
      const dy = maxY - minY;
      let pad = Math.max(dx, dy);
      if (2 * pad < MIN_WIDTH) {
        pad = MIN_WIDTH - pad;
      }
      const bx = minX - pad / 2;
      const by = minY - pad / 2;
      const ex = maxX + pad / 2;
      const ey = maxY + pad / 2;

      const incX = bx < 0 ? -bx : 0;
      const incY = by < 0 ? -by : 0;
      const decX = ex > width ? ex - width : 0;
      const decY = ey > height ? ey - height : 0;

      return [bx, by, ex, ey].map((n, i) => {
        if (i % 2 === 0) {
          return Math.round(n + incX - decX);
        }
        return Math.round(n + incY - decY);
      }) as Clip;
    }

    const { objpx, objpy } = record;
    if (objpx && objpy) {
      const ratio = height / width;
      const sz = width / 8;
      const bx = objpx - sz;
      const by = objpy - sz * ratio;
      const ex = objpx + sz;
      const ey = objpy + sz * ratio;

      const incX = bx < 0 ? -bx : 0;
      const incY = by < 0 ? -by : 0;
      const decX = ex > width ? ex - width : 0;
      const decY = ey > height ? ey - height : 0;

      return [bx, by, ex, ey].map((n, i) => {
        if (i % 2 === 0) {
          return Math.round(n + incX - decX);
        }
        return Math.round(n + incY - decY);
      }) as Clip;
    }

    return [0, 0, width, height] as Clip;
  };*/

  const getRatio = (clip: Clip, w: number, h: number) => {
    const [bx, by, ex, ey] = clip;
    const width = ex - bx;
    const height = ey - by;

    // Calculate the ratio relative to the full image
    const wRatio = w / width;
    const hRatio = h / height;

    return [wRatio, hRatio] as Ratio;
  };

  const drawPolygon = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const { objpoly } = record;
    ctx.beginPath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#00FF00';

    if (!record.objpoly) {
      return;
    }
    const coords = getCoordinates(objpoly);
    if (!coords) {
      return;
    }
    const clip = getClipNew();
    const [bx, by, ex, ey] = clip;
    const [wRatio, hRatio] = getRatio(clip, w, h);

    for (let i = 0; i < coords.length; ++i) {
      const [_u, _v] = coords[i];
      const u = (_u - bx) * wRatio;
      const v = (_v - by) * hRatio;
      if (i === 0) {
        ctx.moveTo(u, v);
      }
      ctx.lineTo(u, v);
    }

    ctx.closePath();
    ctx.stroke();
  };

  const drawPoint = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const { objpx, objpy, objpoly } = record;
    const coords = getCoordinates(objpoly);
    const clip = getClipNew();
    const [bx, by, ex, ey] = clip;
    const [wRatio, hRatio] = getRatio(clip, w, h);

    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#FF0000';
    ctx.fillStyle = '#FF0000';

    const u = (objpx - bx) * wRatio;
    const v = (objpy - by) * hRatio;
    ctx.arc(u, v, PT_RADIUS, 0, 2 * Math.PI);

    ctx.fill();
  };

  const onImgLoad = () => {
    let _ctx: CanvasRenderingContext2D | null = null;
    const hasCanvas = canvasRef && canvasRef.current;
    if (hasCanvas) {
      _ctx = canvasRef.current.getContext('2d');
    }
    if (_ctx === null) {
      return;
    }
    const hasImg = imgRef && imgRef.current;
    const ctx: CanvasRenderingContext2D = _ctx as CanvasRenderingContext2D;
    if (hasCanvas && hasImg) {
      if (!renderWidth || !renderHeight) {
        return;
      }
      canvasRef.current.width = renderWidth;
      canvasRef.current.height = renderHeight;

      ctx.drawImage(imgRef.current, 0, 0, renderWidth, renderHeight);

      if (!dialogHidePolygons) {
        drawPolygon(ctx, renderWidth, renderHeight);
      }
      drawPoint(ctx, renderWidth, renderHeight);
    }
  };

  /** *************************************************************** New functions *********************************************************************** */
  const handleZoomInitial = () => {
    if (record.id !== null) {
      const { objpoly } = record;
      const coords = getCoordinates(objpoly);

      setZoomClip(calculateClip(coords));
    }
  };

  const handleZoomOut = () => {
    if (record) {
      setZoomClip([0, 0, record.width as number, record.height as number]);
    }
  };

  const handleDownload = () => {
    if (!b64) return;

    const fileName = record?.path
        ? String(record.path).split('/').pop() ?? `image_${record.id}`
        : `image_${record?.id ?? 'download'}.png`;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${b64}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadFullImage = () => {
    if (!record?.full_img_id) return;

    const fileName = record?.path
      ? record.path.split('_').pop() ?? `image_${record.id}`
      : `image_${record?.id ?? 'download'}`;

    toggleLoading(true);
    imgService
      .getObjImageOriginal(baseRecordPath, baseRecordId, record.full_img_id as number)
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {})
      .finally(() => toggleLoading(false));
  };

  const getClipNew = () => {
    if (zoomClip !== null) {
      return zoomClip;
    }
    if (record !== null) {
      return [0, 0, record.width, record.height] as Clip;
    }
    return [0, 0, 0, 0] as Clip;
  };

  const calculateClip = (coords: Coordinates) => {
    const { width, height } = record;
    const ratio = height / width;

    if (coords) {
      let minX = width * 2;
      let minY = height * 2;
      let maxX = -minX;
      let maxY = -minY;
      coords.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      const dx = maxX - minX;
      const dy = maxY - minY;
      let pad = Math.max(dx, dy);
      if (2 * pad < MIN_WIDTH) {
        pad = MIN_WIDTH - pad;
      }
      let bx = minX - pad / 2;
      let by = minY - pad / 2;
      let ex = maxX + pad / 2;
      let ey = maxY + pad / 2;

      const sizeX = ex - bx;
      const sizeY = ey - by;
      const maxSize = Math.max(sizeX, sizeY);
      if (maxSize === sizeX) {
        const correctHeight = sizeX * ratio;
        const padHeight = Math.floor((correctHeight - sizeY) / 2);
        by -= padHeight;
        ey += padHeight;
      } else if (maxSize === sizeY) {
        const correctWidth = sizeY / ratio;
        const padWidth = Math.floor((correctWidth - sizeX) / 2);
        bx -= padWidth;
        ex += padWidth;
      }

      const incX = bx < 0 ? -bx : 0;
      const incY = by < 0 ? -by : 0;
      const decX = ex > width ? ex - width : 0;
      const decY = ey > height ? ey - height : 0;
      return [bx, by, ex, ey].map((n, i) => {
        if (i % 2 === 0) {
          return Math.max(0, Math.min(width, Math.round(n + incX - decX)));
        }
        return Math.max(0, Math.min(height, Math.round(n + incY - decY)));
      }) as Clip;
    }

    const { objpx, objpy } = record;
    if (objpx && objpy) {
      const sz = width / 8;
      const bx = objpx - sz;
      const by = objpy - sz * ratio;
      const ex = objpx + sz;
      const ey = objpy + sz * ratio;

      const incX = bx < 0 ? -bx : 0;
      const incY = by < 0 ? -by : 0;
      const decX = ex > width ? ex - width : 0;
      const decY = ey > height ? ey - height : 0;

      return [bx, by, ex, ey].map((n, i) => {
        if (i % 2 === 0) {
          return Math.round(n + incX - decX);
        }
        return Math.round(n + incY - decY);
      }) as Clip;
    }

    return [0, 0, width, height] as Clip;
  };

  const getScaleFactor = (width: number, height: number) => {
    if (!renderWidth || !renderHeight) return 1;
    return Math.min(renderWidth / width, renderHeight / height);
  };

  const calculateRealCoordinates = (
    x: number,
    y: number,
    w: number,
    h: number,
    currentZoomClip: Clip,
    img: DCRecord,
    enforceAspectRatio: boolean = true
  ) => {
    if (currentZoomClip && img && img.width && img.height) {
      const [bx, by, ex, ey] = currentZoomClip;
      const clip_width = ex - bx;
      const clip_height = ey - by;

      const width = img.width as number;
      const height = img.height as number;

      const aspectRatio = width / height;
      const scaleFactor = getScaleFactor(width, height);

      let zoom_start_x = Math.floor(x);
      let zoom_start_y = Math.floor(y);
      let zoom_end_x = Math.floor(x + w);
      let zoom_end_y = Math.floor(y + h);

      let clip_start_x = Math.floor((zoom_start_x / (scaleFactor * width)) * clip_width);
      let clip_start_y = Math.floor((zoom_start_y / (scaleFactor * height)) * clip_height);
      let clip_end_x = Math.floor((zoom_end_x / (scaleFactor * width)) * clip_width);
      let clip_end_y = Math.floor((zoom_end_y / (scaleFactor * height)) * clip_height);

      // Translate back to image space
      let real_start_x = bx + clip_start_x;
      let real_start_y = by + clip_start_y;
      let real_end_x = bx + clip_end_x;
      let real_end_y = by + clip_end_y;

      // Normalize order
      if (real_end_x < real_start_x) [real_start_x, real_end_x] = [real_end_x, real_start_x];
      if (real_end_y < real_start_y) [real_start_y, real_end_y] = [real_end_y, real_start_y];

      // Only enforce aspect ratio if needed (zoom)
      if (enforceAspectRatio) {
        let newWidth = real_end_x - real_start_x;
        let newHeight = real_end_y - real_start_y;
        let currentRatio = newWidth / newHeight;

        if (currentRatio > aspectRatio) {
          // too wide → adjust width
          newWidth = Math.round(newHeight * aspectRatio);
          real_end_x = real_start_x + newWidth;
        } else {
          // too tall → adjust height
          newHeight = Math.round(newWidth / aspectRatio);
          real_end_y = real_start_y + newHeight;
        }
      }

      // Clamp to image bounds (works for both zoom + crop)
      if (real_start_x < 0) {
        real_end_x -= real_start_x;
        real_start_x = 0;
      }
      if (real_start_y < 0) {
        real_end_y -= real_start_y;
        real_start_y = 0;
      }
      if (real_end_x > width) {
        const diff = real_end_x - width;
        real_start_x -= diff;
        real_end_x = width;
      }
      if (real_end_y > height) {
        const diff = real_end_y - height;
        real_start_y -= diff;
        real_end_y = height;
      }

      real_start_x = Math.max(0, real_start_x);
      real_start_y = Math.max(0, real_start_y);

      return [real_start_x, real_start_y, real_end_x, real_end_y] as Clip;
    }
    return null;
  };

  const handleStartDrawing = () => {};

  const handleDrawZoomBox = (x: number, y: number, w: number, h: number) => {
    if (zoomClip && record) {
      const newZoomClip = calculateRealCoordinates(x, y, w, h, zoomClip, record);
      if (newZoomClip) {
        setZoomClip(newZoomClip);
      }
    }
  };

  useEffect(() => {
    if (record.id !== null) {
      const { objpoly } = record;
      const coords = getCoordinates(objpoly);
      setZoomClip(calculateClip(coords));
      setFullB64('');
      setInitialB64('');
    }
  }, [imageId]);

  useEffect(() => {
    if (baseRecordId && record && record.id && zoomClip !== null && renderWidth && renderHeight) {
      const reqClip = getClipNew();
      const isFullImage = !!(reqClip[0] === 0 && reqClip[1] === 0 && reqClip[2] === record.width && reqClip[3] === record.height);

      const { objpoly } = record;
      const coords = getCoordinates(objpoly);
      const initialClip = calculateClip(coords);
      const isInitialImage = !!(
        reqClip[0] === initialClip[0] &&
        reqClip[1] === initialClip[1] &&
        reqClip[2] === initialClip[2] &&
        reqClip[3] === initialClip[3]
      );

      if (isFullImage && fullB64 !== '') {
        setB64(fullB64);
      } else if (isInitialImage && initialB64 !== '') {
        setB64(initialB64);
      } else if ((isFullImage && fullB64 === '') || (isInitialImage && initialB64 === '') || (!isFullImage && !isInitialImage)) {
        setIsLoading(true);
        imgService
          .getObjImage(baseRecordPath, baseRecordId, record.id as number, renderWidth, reqClip)
          .then((resp) => {
            if (resp.success) {
              setB64(resp.data.image);
              if (isFullImage) {
                setFullB64(resp.data.image);
              }
              if (isInitialImage) {
                setInitialB64(resp.data.image);
              }
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [zoomClip, renderWidth, renderHeight]);

  const zoomedOutFully =
    record && zoomClip && zoomClip[0] === 0 && zoomClip[1] === 0 && zoomClip[2] === record.width && zoomClip[3] === record.height;

  const imageLoaded = !!(!isLoading && record && b64.length);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && b64) {
      onImgLoad();
    }
  }, [b64, renderWidth, renderHeight]);

  return (
    <Dialog
      open
      maxWidth={false}
      fullScreen={!smUp}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: smUp ? (renderWidth ? `${renderWidth}px` : 'auto') : '100vw',
            maxWidth: 'none',
            display: 'flex',
            flexDirection: 'column',
            height: smUp ? 'auto' : '100dvh',
          },
        },
      }}
    >
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t('titles.image')}</DialogToolbarHeading>
          <ToolbarFillContent />

          <DialogToolbarMenuButton>
            <DialogToolbarMenuItemDownload onClick={(handleDownload)} />
            {record?.full_img_id && (
              <DialogToolbarMenuItemDownload onClick={handleDownloadFullImage} label={String(t("buttons.download_full"))}/>
            )}
          </DialogToolbarMenuButton>
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: smUp ? (renderWidth ? `${renderWidth}px` : 'auto') : '100%',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          ref={wrapperRef}
          sx={{
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {imageLoaded ? (
            <div style={{ position: 'relative', width: `${renderWidth}px`, height: `${renderHeight}px` }}>
              <img
                ref={imgRef}
                src={`data:image/png;base64, ${b64}`}
                alt=""
                style={{
                  visibility: 'hidden',
                  display: 'block',
                  position: 'absolute',
                  top: '0px',
                  left: '0px',
                  height: `${renderHeight}px`,
                  width: `${renderWidth}px`,
                  zIndex: 100,
                }}
                onLoad={() => onImgLoad()}
              />
              <Canvas
                ref={canvasRef}
                baseDraw={onImgLoad}
                onStartDrawing={handleStartDrawing}
                onDrawZoomBox={handleDrawZoomBox}
                onWheelDown={handleZoomOut}
                onCanvasClick={() => {}}
                drawClip={zoomClip}
                containerWidth={renderWidth}
                containerHeight={renderHeight}
                ratio={(record.width as number) / (record.height as number)}
                setIsDrawing={setIsDrawing}
              />

              <Tooltip title={t('buttons.zoom_object') as string}>
                <Fab
                  color="primary"
                  size="small"
                  sx={{ position: 'absolute', top: 16, right: 16, transform: { xs: 'scale(0.85)', md: 'unset' } }}
                  onClick={handleZoomInitial}
                >
                  <CenterFocusWeakIcon />
                </Fab>
              </Tooltip>
              <Tooltip title={t('buttons.zoom_out') as string}>
                <Fab
                  color="primary"
                  size="small"
                  sx={{ position: 'absolute', bottom: 16, right: 16, transform: { xs: 'scale(0.85)', md: 'unset' } }}
                  onClick={handleZoomOut}
                  disabled={Boolean(zoomedOutFully)}
                >
                  <ZoomOutIcon />
                </Fab>
              </Tooltip>
            </div>
          ) : (
            <Skeleton
              variant="rectangular"
              width={renderWidth || Math.round(windowWidth * (smUp ? 0.5 : 1))}
              height={renderHeight || Math.round(windowHeight * (smUp ? 0.7 : 0.6))}
              animation="wave"
              style={{ display: 'inline-block' }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {imageLoaded ? (
          <Typography variant="caption">{mdUp ? t('messages.how_to_zoom') : t('messages.how_to_zoom_mobile')}</Typography>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default PhotoCentricImageDialog;
